import { parseDecimalInput } from "./decimalInput";

/**
 * Времињата се чуваат и се праќаат до API-то во МИНУТИ (планирањето чита минути),
 * а корисникот ги гледа и ги внесува како ЧАСОВИ:МИНУТИ — „0:10“, „1:30“ (одлука 2026-10-07).
 * Сите претворања минути ⇄ часови одат САМО низ овие две функции — без `*60` / `/60` на друго место.
 */

/** Минути → „Ч:ММ“: 10 → `0:10`, 90 → `1:30`, 960 → `16:00`. Празно/невалидно → ``. */
export const minutesToHoursText = (minutes: unknown): string => {
  const value = parseDecimalInput(minutes);
  if (value === null) return "";
  const total = Math.round(value);
  // A negative span (Крај пред Почеток on RN) stays visible as „-0:10“, not „-1:-10“.
  const sign = total < 0 ? "-" : "";
  const abs = Math.abs(total);
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, "0")}`;
};

/**
 * Внес → цели минути. `1:30` / `0:10` = часови:минути; број без `:` = цели или децимални часови
 * (`2` → 120, `0,5` → 30). Празно/невалидно → null.
 */
export const hoursTextToMinutes = (text: unknown): number | null => {
  const raw = String(text ?? "").trim();
  if (!raw) return null;
  const hmm = /^(\d+):(\d{1,2})$/.exec(raw);
  if (hmm) return Number(hmm[1]) * 60 + Number(hmm[2]);
  const hours = parseDecimalInput(raw);
  if (hours === null || hours < 0) return null;
  return Math.round(hours * 60);
};

// ЕМ од шифрарникот што значи „часови“ — исто множество како серверот (server/utils/timeUnits.js).
const HOUR_UNIT_TEXTS = new Set(["h", "hh", "ч", "чч", "час", "часа", "часови", "hour", "hours", "h:mm", "hh:mm", "ч:мм"]);

/** Дали единицата (по шифра или назив) е час — тогаш времето се прикажува како Ч:ММ. */
export const isHourUnit = (unit: { code?: string; name?: string; unitMeasurment?: string } | null | undefined): boolean =>
  !!unit && [unit.code, unit.name, unit.unitMeasurment].some((text) => HOUR_UNIT_TEXTS.has(String(text ?? "").trim().toLowerCase()));

/** Минути → текст според ЕМ: час → `0:30`, друго (min) → `30`. */
export const minutesToUnitText = (minutes: unknown, hourUnit: boolean): string => {
  if (hourUnit) return minutesToHoursText(minutes);
  const value = parseDecimalInput(minutes);
  return value === null ? "" : String(Math.round(value));
};

/** Внес → цели минути според ЕМ. `0:30` е секогаш часови:минути; број без `:` е час или минута по ЕМ. */
export const unitTextToMinutes = (text: unknown, hourUnit: boolean): number | null => {
  const raw = String(text ?? "").trim();
  if (hourUnit || raw.includes(":")) return hoursTextToMinutes(raw);
  const value = parseDecimalInput(raw);
  return value === null || value < 0 ? null : Math.round(value);
};
